import { graphql, withPrefix } from "gatsby"
import styled, { css } from "styled-components"

import RichText from "@components/rich-text/renderer"
import { FastlyImage } from "@components/FastlyImage"
import { Box, Button, Container, Grid } from "@fastly/consistently"
import { PreFooter } from "@fastly/consistently-vanilla"
import { text as text_cn, links as links_cn } from "@components/PreFooter/PreFooter.module.css"
import BlockCarousel from "@components/page/block-carousel"
import ProductCardsGroup from "@components/ProductCardsGroup/ProductCardsGroup"

import "../../fragments/Page"
import { MikanSpan } from "@components/Mikan"
import { findById } from "@utils/contentful"
import reducePageContent from "@utils/reducePageContent"

import { logos as logos_cn, logosList as logosList_cn, card } from "./partners.module.css"

const LogoContainer = styled(Container)`
  ${({ theme }) => css`
    @media (min-width: ${theme.breakpoints.lg}) {
      background: url(/g-static/nimbus/partner-logo-nimbus.svg) no-repeat top;
    }
  `}
`

const Main = styled.main`
  position: relative;
`

const PartnersHero = styled(Container)`
  ${({ theme }) => css`
    display: grid;
    align-items: center;
    gap: ${theme.space["5xl"]};

    a {
      margin-block: var(--size-space-md);
    }

    @media (min-width: ${theme.breakpoints.md}) {
      grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr);

      &::before {
        background: url(/g-static/nimbus/partners-top-nimbus.svg) no-repeat;
        content: "";
        width: 361px;
        height: 296px;
        position: absolute;
        top: -100px;
        right: 0;
        z-index: -1;
      }

      &::after {
        content: "";
        width: 181px;
        height: 560px;
        position: absolute;
        top: 360px;
        right: 0;
        background: url(/g-static/nimbus/partners-bottom-nimbus.svg) no-repeat;
      }
    }

    & p:first-child,
    & div:first-of-type > p:first-child {
      font-weight: 700;
      color: var(--grey-60);
      text-transform: uppercase;
      letter-spacing: -0.22px;
    }
  `}
`

const Partners = ({ data }) => {
  const page = data.page
  const pageContent = reducePageContent(page)

  const hero = pageContent["68mN6euXABtJOd4kZo5yVg"]
  const heroText = findById(hero, "6QYhIv69W86TnZP9Qob431")
  const heroImage = findById(hero, "685jIfU6MOG8jKTrR7W5Qp")

  const logoSection = pageContent["69A0MshcE1Mj5M1npdhGMd"]
  const logos = findById(logoSection, "2RJn5zGGrvhQEGPyOxThZ6").section_content
  const logoHeading = findById(logoSection, "4n5wRO2tTVhXAEBx8fx6q7").text
  const featuredPartnersButton = findById(logoSection, "WvFOwIF5oopixiaBsy1ri")

  const cards = pageContent["6vsdRSskgIsF6OfFfDzTUQ"]

  const testimonials = pageContent["2zwjzpQu2bBCemzSj3koZj"]

  const quadCardGroup = pageContent["6lpQIOCNu59OZI7jKt3JqN"]
  const quadCardGroupText = findById(quadCardGroup, "1Eww4uRPt2ih62eywjOhJj")
  const quadCardGroupProps = findById(quadCardGroup, "3BTAXp7QetlFRo7qoqBdRk").section_content

  const prefooter = pageContent["1437X6Rbdi44RzPpVmtsjC"]
  const prefooterText = findById(prefooter, "6gebsLjNMxGqU3QGRVMlrd")
  const prefooterCta = findById(prefooter, "4ri43nlU0c2Nh360kBIlPC")

  return (
    <>
      <Main>
        <PartnersHero py="3xl">
          <div>
            <RichText body={heroText.rtContent} key={heroText.contentful_id} mikan={heroText.mikan} />
          </div>
          <Box>
            <FastlyImage src={heroImage.content.file.url} alt="" />
          </Box>
        </PartnersHero>

        {/* Testimonial Carousel */}
        <Container pt="4xl" pb="256px">
          <BlockCarousel theme="dark" autoScroll autoScrollInterval={10000}>
            {testimonials.map((testimonial) => {
              return (
                <>
                  <div className="carouselLogo">
                    <FastlyImage src={testimonial.person.company.logo.file.url} alt="" />
                  </div>
                  <blockquote>
                    <MikanSpan justify>{testimonial.quote.quote}</MikanSpan>
                  </blockquote>
                  <div className="name">
                    <MikanSpan>{testimonial.person.name}</MikanSpan>
                  </div>
                  <div className="title">
                    <MikanSpan>{testimonial.person.title}</MikanSpan>
                  </div>
                </>
              )
            })}
          </BlockCarousel>
        </Container>

        {/* Logos*/}
        <LogoContainer pt="4xl" background="var(--grey-20)" overflow="visible">
          <div style={{ transform: "translateY(-128px)" }}>
            <div className={logos_cn}>
              <h2>{logoHeading}</h2>
              <div className={logosList_cn}>
                {logos.map((l) => (
                  <FastlyImage
                    src={l.content.file.url}
                    params={{ height: 48, auto: "avif" }}
                    alt=""
                    key={l.contentful_id}
                  />
                ))}
              </div>
              <Button
                variant={featuredPartnersButton.variant}
                url={withPrefix(featuredPartnersButton.url)}
                icon={featuredPartnersButton.iconString}
              >
                <MikanSpan text={featuredPartnersButton.buttonText} />
              </Button>
            </div>

            <Grid md={2} gridGap={{ _: "xl", lg: "4xl" }} mx="auto" maxWidth="760px">
              {cards.map((c) => (
                <div className={card} key={c.contentful_id}>
                  <RichText body={c.rtContent} key={c.contentful_id} mikan={c.mikan} />
                </div>
              ))}
            </Grid>
          </div>
        </LogoContainer>

        {/* quadCard */}
        <Container py="6xl">
          <ProductCardsGroup
            type="quadCard"
            text={quadCardGroupText}
            propsValues={quadCardGroupProps}
            iconsProps={{ color: "blue-dark", width: "52px", height: "52px" }}
          />
        </Container>
        <PreFooter id="preFooter">
          <div className={text_cn}>
            <RichText body={prefooterText.rtContent} mikan={prefooterText.mikan} />
          </div>
          <div className={links_cn}>
            <RichText body={prefooterCta.rtContent} mikan={prefooterCta.mikan} />
          </div>
        </PreFooter>
      </Main>
    </>
  )
}

export default Partners

export const query = graphql`
  query ($contentful_id: String!) {
    page: contentfulPage(contentful_id: { eq: $contentful_id }) {
      ...Page
    }
  }
`

export { Head } from "@components/Head/Head"
